import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as actions from '../../../OrdersActions';
import notify from '../../../../../shared/feedback/notifications';
import { useQuery } from 'react-query';
import OrdersApi from '../../../OrdersApi';
import ListSkeleton from './components/ListSkeleton';
import { useTelemetryContext } from '../../../../../telemetry/telemetryContext';
import OrderListContainer from './components/OrderListContainer';
import { Translate } from '../../../../../shared/LanguageService';

const dateToString = (date) => {
    // return date in yyyy-mm-dd format
    const offset = date.getTimezoneOffset();
    date = new Date(date.getTime() - offset * 60 * 1000);
    return date.toISOString().split('T')[0];
};

interface IFilters {
    orderType: number | null;
    search: string;
    brokerAgent: number | null;
    orderPlacedAtStart: Date | null;
    orderPlacedAtEnd: Date | null;
}

interface IProps {
    user: any;
    filters: IFilters;
    actions: any;
}

function OrdersPage(props: IProps) {
    let todayDate = new Date();
    let dateInterval = new Date();
    let site = window.appConfig.site;
    site === 'se360'
        ? dateInterval.setMonth(todayDate.getMonth() - 6)
        : dateInterval.setMonth(todayDate.getMonth() - 12);

    const { filters } = props;

    const [selectedOrderType, setSelectedOrderType] = useState<number | null>(filters.orderType);
    const [searchPhrase, setSearchPhrase] = useState<string>(filters.search);
    const [selectedBrokerAgent, setSelectedBrokerAgent] = useState<number | null>(
        filters.brokerAgent
    );
    const [orderPlacedAtStart, setOrderPlacedAtStart] = useState<Date | null>(
        filters.orderPlacedAtStart ? filters.orderPlacedAtStart : dateInterval
    );
    const [orderPlacedAtEnd, setOrderPlacedAtEnd] = useState<Date | null>(
        filters.orderPlacedAtEnd ? filters.orderPlacedAtEnd : todayDate
    );

    const [refetchInterval, setRefetchInterval] = React.useState(600000);

    const { logger } = useTelemetryContext();
    const { isLoading, isError, error, data, refetch } = useQuery(
        ['brokerAgentOrders'],
        () =>
            OrdersApi.getOrdersV2(dateToString(orderPlacedAtStart), dateToString(orderPlacedAtEnd)),
        {
            refetchInterval,
            onSuccess: (data) => {
                if (data.progress === 100) {
                    setRefetchInterval(false);
                }
            }
        }
    );

    useEffect(() => {
        const filtersPrefilled =
            filters.orderType ||
            filters.search.length !== 0 ||
            filters.brokerAgent ||
            filters.orderPlacedAtStart
                ? filters.orderPlacedAtStart.getTime() === orderPlacedAtStart.getTime()
                : false || filters.orderPlacedAtEnd
                ? filters.orderPlacedAtEnd.getTime() === orderPlacedAtEnd.getTime()
                : false;

        if (filtersPrefilled) {
            notify.info(Translate('orders/list/advance/toast/info/has-saved-filter'));
        }
    }, []);

    useEffect(() => {
        refetch();
    }, [orderPlacedAtStart, orderPlacedAtEnd]);

    useEffect(() => {
        const filter: IFilters = {
            orderType: selectedOrderType,
            search: searchPhrase,
            brokerAgent: selectedBrokerAgent,
            orderPlacedAtStart: orderPlacedAtStart,
            orderPlacedAtEnd: orderPlacedAtEnd
        };

        props.actions.saveFilters(filter);
    }, [
        selectedOrderType,
        searchPhrase,
        selectedBrokerAgent,
        orderPlacedAtStart,
        orderPlacedAtEnd
    ]);

    if (isError) {
        const additionalInfo = {
            origin: 'Version 2 of Broker agents order list'
            // TODO: userInfo
        };
        logger.logException(error, additionalInfo);
    }

    if (isLoading) {
        return <ListSkeleton />;
    }

    const currentDate = new Date();
    const date3YearsAgo = new Date(
        currentDate.getFullYear() - 3,
        currentDate.getMonth(),
        currentDate.getDate()
    );
    return (
        <div>
            <OrderListContainer
                availableOrderTypes={data.availableOrderTypes}
                availableBrokerAgents={data.availableBrokerAgents}
                orderCollectionsWithDate={data.orderCollections}
                earliestAvailableOrderDate={date3YearsAgo}
                user={props.user}
                selectedOrderType={selectedOrderType}
                searchPhrase={searchPhrase}
                selectedBrokerAgent={selectedBrokerAgent}
                orderPlacedAtStart={orderPlacedAtStart}
                orderPlacedAtEnd={orderPlacedAtEnd}
                setSelectedOrderType={(id) => setSelectedOrderType(id)}
                setSearchPhrase={(search) => setSearchPhrase(search)}
                setSelectedBrokerAgent={(id) => setSelectedBrokerAgent(id)}
                setOrderPlacedAtStart={(date) => setOrderPlacedAtStart(date)}
                setOrderPlacedAtEnd={(date) => setOrderPlacedAtEnd(date)}
            />
        </div>
    );
}

function mapStateToProps(state) {
    return {
        user: state.user,
        filters: state.orders.filters.agentBrokerFilters
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            saveFilters: (filter: IFilters) => dispatch(actions.saveAgentBrokerFilters(filter))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(OrdersPage);
